import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AlertCircle, ArrowUp, History, Loader, RefreshCw } from 'lucide-react';
import { subDays } from 'date-fns';
import { elasticsearchService } from '../services/elasticsearchService';
import {
  ShopChangelogDoc,
  ShopChangelogFilters as Filters,
  ShopChangelogHit,
} from '../types/shopChangelog';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { ShopChangelogFilters } from '../components/shopChangelog/ShopChangelogFilters';
import { ShopChangelogFeedItem } from '../components/shopChangelog/ShopChangelogFeedItem';
import { LogPanelSkeleton } from '../components/LogPanelSkeleton';
import { usernameCacheService } from '../services/usernameCache';
import { cn } from '../utils/cn';
import { PageHeader } from '../components/ui/PageHeader';

const PAGE_SIZE = 50;
const POLL_MS = 30_000;
const FILTER_DEBOUNCE_MS = 500;
/** Pixels of scroll-from-top below which we treat the user as "at top" and merge new docs silently. */
const SCROLL_TOP_THRESHOLD = 120;

const defaultFromDate = () => subDays(new Date(), 7);

const initialFilters = (): Filters => ({
  networkIds: [],
  clusterIds: [],
  userIds: [],
  affectedPageIds: [],
  dateRange: { from: defaultFromDate(), to: new Date() },
  destructiveOnly: false,
});

interface BuildQueryArgs {
  filters: Filters;
  fromOffset: number;
  size?: number;
}

function buildQuery({ filters, fromOffset, size = PAGE_SIZE }: BuildQueryArgs): object {
  const must: any[] = [];

  if (filters.networkIds.length)      must.push({ terms: { networkId: filters.networkIds } });
  if (filters.clusterIds.length)      must.push({ terms: { clusterId: filters.clusterIds } });
  if (filters.userIds.length)         must.push({ terms: { userId: filters.userIds } });
  if (filters.affectedPageIds.length) must.push({ terms: { affectedPageIds: filters.affectedPageIds } });

  must.push({
    range: {
      '@timestamp': {
        gte: filters.dateRange.from.toISOString(),
        lte: filters.dateRange.to.toISOString(),
      },
    },
  });

  if (filters.destructiveOnly) {
    must.push({
      bool: {
        should: [
          { range: { 'totals.pagesRemoved':   { gt: 0 } } },
          { range: { 'totals.entriesRemoved': { gt: 0 } } },
        ],
        minimum_should_match: 1,
      },
    });
  }

  return {
    size,
    from: fromOffset,
    sort: [{ '@timestamp': { order: 'desc' } }],
    query: must.length ? { bool: { must } } : { match_all: {} },
    track_total_hits: true,
  };
}

export function ShopChangelogView() {
  useDocumentTitle('Shop Changelog');

  const [filters, setFilters] = useState<Filters>(() => initialFilters());
  const [hits, setHits] = useState<ShopChangelogHit[]>([]);
  const [pendingHits, setPendingHits] = useState<ShopChangelogHit[]>([]);
  const [totalHits, setTotalHits] = useState(0);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isPolling, setIsPolling] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [newlyMergedIds, setNewlyMergedIds] = useState<Set<string>>(new Set());

  // Refs for closure-stable values used inside polling/scroll handlers.
  const filtersRef = useRef(filters);
  filtersRef.current = filters;
  const hitsRef = useRef(hits);
  hitsRef.current = hits;
  const pendingHitsRef = useRef(pendingHits);
  pendingHitsRef.current = pendingHits;

  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const updateFilters = useCallback((next: Partial<Filters>) => {
    setFilters((prev) => ({ ...prev, ...next }));
  }, []);

  // ── Prefetch usernames for any UUID-typed users so MC heads pop in fast ───
  const prefetchUsernames = useCallback((rows: ShopChangelogHit[]) => {
    const uuids = rows
      .map((h) => h._source)
      .filter((d) => d.userIdType === 'uuid' && d.userId)
      .map((d) => d.userId as string);
    if (uuids.length) {
      void usernameCacheService.prefetchUsernames(uuids);
    }
  }, []);

  // ── Initial / filter-change fetch (debounced) ─────────────────────────────
  useEffect(() => {
    const handle = window.setTimeout(async () => {
      setIsInitialLoading(true);
      setError(null);
      setHits([]);
      setPendingHits([]);
      setNewlyMergedIds(new Set());
      try {
        const data = await elasticsearchService.getShopChangelog(
          buildQuery({ filters: filtersRef.current, fromOffset: 0 }),
        );
        setHits(data.hits.hits);
        setTotalHits(data.hits.total.value);
        prefetchUsernames(data.hits.hits);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to load shop changelog.');
      } finally {
        setIsInitialLoading(false);
      }
    }, FILTER_DEBOUNCE_MS);
    return () => window.clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, prefetchUsernames]);

  // ── Load more (infinite scroll) ───────────────────────────────────────────
  const loadMore = useCallback(async () => {
    if (isLoadingMore) return;
    if (hitsRef.current.length >= totalHits) return;
    setIsLoadingMore(true);
    try {
      const data = await elasticsearchService.getShopChangelog(
        buildQuery({ filters: filtersRef.current, fromOffset: hitsRef.current.length }),
      );
      // De-dup by _id in case ingestion timing pushes duplicate sort keys.
      const existing = new Set(hitsRef.current.map((h) => h._id));
      const merged = [...hitsRef.current, ...data.hits.hits.filter((h) => !existing.has(h._id))];
      setHits(merged);
      setTotalHits(data.hits.total.value);
      prefetchUsernames(data.hits.hits);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load more entries.');
    } finally {
      setIsLoadingMore(false);
    }
  }, [isLoadingMore, totalHits, prefetchUsernames]);

  // IntersectionObserver sentinel for load-more.
  const loadMoreSentinelRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const node = loadMoreSentinelRef.current;
    if (!node) return;
    const io = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && !isLoadingMore && hitsRef.current.length < totalHits) {
        void loadMore();
      }
    });
    io.observe(node);
    return () => io.disconnect();
  }, [loadMore, isLoadingMore, totalHits]);

  // ── Polling for new entries ───────────────────────────────────────────────
  const pollNow = useCallback(async () => {
    setIsPolling(true);
    try {
      const data = await elasticsearchService.getShopChangelog(
        buildQuery({ filters: filtersRef.current, fromOffset: 0 }),
      );
      setTotalHits(data.hits.total.value);

      const knownIds = new Set([
        ...hitsRef.current.map((h) => h._id),
        ...pendingHitsRef.current.map((h) => h._id),
      ]);
      const fresh = data.hits.hits.filter((h) => !knownIds.has(h._id));
      if (!fresh.length) return;

      prefetchUsernames(fresh);

      const container = scrollContainerRef.current;
      const atTop = !container || container.scrollTop <= SCROLL_TOP_THRESHOLD;

      if (atTop) {
        // Merge in directly; mark them new for a brief highlight.
        setHits((prev) => {
          const existing = new Set(prev.map((h) => h._id));
          return [...fresh.filter((h) => !existing.has(h._id)), ...prev];
        });
        setNewlyMergedIds((prev) => {
          const next = new Set(prev);
          fresh.forEach((h) => next.add(h._id));
          return next;
        });
        // Clear the "new" highlight after 4s so it doesn't stay forever.
        const ids = fresh.map((h) => h._id);
        window.setTimeout(() => {
          setNewlyMergedIds((prev) => {
            const next = new Set(prev);
            ids.forEach((id) => next.delete(id));
            return next;
          });
        }, 4000);
      } else {
        // Buffer behind a banner.
        setPendingHits((prev) => {
          const existing = new Set(prev.map((h) => h._id));
          return [...fresh.filter((h) => !existing.has(h._id)), ...prev];
        });
      }
    } catch {
      // Silent — we don't want to surface a polling failure as a hard error.
    } finally {
      setIsPolling(false);
    }
  }, [prefetchUsernames]);

  useEffect(() => {
    const id = window.setInterval(() => {
      if (document.visibilityState !== 'visible') return;
      void pollNow();
    }, POLL_MS);
    return () => window.clearInterval(id);
  }, [pollNow]);

  const flushPending = useCallback(() => {
    if (pendingHitsRef.current.length === 0) return;
    const flushed = pendingHitsRef.current;
    setHits((prev) => {
      const existing = new Set(prev.map((h) => h._id));
      return [...flushed.filter((h) => !existing.has(h._id)), ...prev];
    });
    setPendingHits([]);
    setNewlyMergedIds((prev) => {
      const next = new Set(prev);
      flushed.forEach((h) => next.add(h._id));
      return next;
    });
    const ids = flushed.map((h) => h._id);
    window.setTimeout(() => {
      setNewlyMergedIds((prev) => {
        const next = new Set(prev);
        ids.forEach((id) => next.delete(id));
        return next;
      });
    }, 4000);
    scrollContainerRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const defaultFrom = useMemo(() => defaultFromDate(), []);

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Curators"
        title="Shop Changelog"
        description="Continuous feed of every change made to admin shops via the Curators editor. Updates every 30 seconds."
        icon={History}
        meta={
          <>
            {isPolling && (
              <span className="inline-flex items-center gap-1">
                <RefreshCw className="w-3 h-3 animate-spin" />
                polling…
              </span>
            )}
            <span className="badge badge-neutral">{totalHits.toLocaleString()} total</span>
          </>
        }
      />

      {/* Filters */}
      <div className="glass-card p-3 md:p-4">
        <ShopChangelogFilters filters={filters} onChange={updateFilters} defaultFrom={defaultFrom} />
      </div>

      {/* Feed */}
      <div className="glass-card overflow-hidden relative">
        {/* Pending-changes banner */}
        {pendingHits.length > 0 && (
          <button
            type="button"
            onClick={flushPending}
            className={cn(
              'sticky top-0 z-20 w-full flex items-center justify-center gap-2 py-2',
              'bg-accent-500/30 hover:bg-accent-500/40 text-accent-100 font-semibold text-sm',
              'border-b border-accent-400/40 backdrop-blur transition-colors',
            )}
          >
            <ArrowUp className="w-4 h-4" />
            {pendingHits.length} new change{pendingHits.length === 1 ? '' : 's'} — click to view
          </button>
        )}

        <div
          ref={scrollContainerRef}
          className="overflow-y-auto"
          style={{ maxHeight: 'calc(100vh - 320px)', minHeight: '420px' }}
        >
          {isInitialLoading ? (
            <div className="p-4">
              <LogPanelSkeleton />
            </div>
          ) : error ? (
            <div className="p-8 text-center">
              <AlertCircle className="w-10 h-10 text-red-400 mx-auto mb-3" />
              <h2 className="text-sm font-semibold text-white mb-1">Failed to load shop changelog</h2>
              <p className="text-sm text-glass-400 mb-4 max-w-md mx-auto break-all">{error}</p>
              <button
                onClick={() =>
                  // Re-trigger the debounced effect by toggling a no-op date update.
                  setFilters((f) => ({ ...f, dateRange: { ...f.dateRange } }))
                }
                className="glass-button"
              >
                Retry
              </button>
            </div>
          ) : hits.length === 0 ? (
            <div className="p-12 text-center text-glass-400">
              No shop changes match the current filters.
            </div>
          ) : (
            <>
              {hits.map((hit) => (
                <ShopChangelogFeedItem
                  key={hit._id}
                  doc={hit._source as ShopChangelogDoc}
                  isNew={newlyMergedIds.has(hit._id)}
                />
              ))}
              {hits.length < totalHits && (
                <div ref={loadMoreSentinelRef} className="p-4 text-center text-sm text-glass-400">
                  {isLoadingMore ? (
                    <span className="inline-flex items-center gap-2">
                      <Loader className="w-4 h-4 animate-spin" />
                      Loading more…
                    </span>
                  ) : (
                    'Loading more…'
                  )}
                </div>
              )}
              {hits.length >= totalHits && hits.length > 0 && (
                <div className="p-4 text-center text-xs text-glass-500">
                  End of results · {hits.length.toLocaleString()} shown
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
